import { defineStore } from "pinia";
import axios from "axios";
import {
  BACKEND_URL,
  TASKS_ME_ENDPOINT,
  TASKS_META_ENDPOINT,
  TASKS_LIST_ENDPOINT,
  TASKS_GET_ENDPOINT,
  TASKS_CREATE_ENDPOINT,
  TASKS_UPDATE_ENDPOINT,
  TASKS_TRANSITION_ENDPOINT,
  TASKS_ASSIGN_ENDPOINT,
  TASKS_DUE_ENDPOINT,
  TASKS_BLOCK_ENDPOINT,
  TASKS_SUBTASK_ENDPOINT,
  TASKS_PEOPLE_ENDPOINT,
  TASKS_PERSON_ENDPOINT,
  TASKS_FILE_UPLOAD_ENDPOINT,
  TASKS_FILE_DELETE_ENDPOINT,
} from "../variables.js";

// RPD Tasks.
//
// Two things about this store are deliberate and worth not undoing:
//
// 1. It does not decide what the user may do. Capabilities come from
//    api/tasks/me (`session.can`) and, per card, from the `gates` block on
//    api/tasks/get. Reimplementing the permission matrix or the transition
//    rules here would give us two copies that drift, and the UI would start
//    promising transitions the server refuses. The authority is
//    inc/rpd_tasks.gates.inc.
//
// 2. Only `meta` and the last-used filters are persisted. `rows` is
//    server-derived and cheap to refetch; persisting a board would serve stale
//    cards after a reload for no benefit, and put a wide slice of work data
//    into localStorage.
const url = (endpoint, arg) =>
  arg === undefined || arg === null
    ? `${BACKEND_URL}${endpoint}`
    : `${BACKEND_URL}${endpoint}/${encodeURIComponent(arg)}`;

// The backend answers a preflight with Access-Control-Allow-Headers, so plain
// JSON posts work here — no text/plain workaround, unlike Market Overview.
const post = (endpoint, arg, body) =>
  axios.post(url(endpoint, arg), body || {}, { withCredentials: true });

const get = (endpoint, arg, params) =>
  axios.get(url(endpoint, arg), { params: params || {}, withCredentials: true });

// The endpoints answer { status: 'success' | 'error', ... }. An error carries a
// user-facing `message`, and a refused transition also carries `missing` and
// `missing_labels` naming what is outstanding. Surface the message; never
// invent one.
const unwrap = (res) => {
  const data = res && res.data ? res.data : {};
  if (data.status !== "success") {
    const err = new Error(data.message || "The request failed.");
    err.missing = data.missing || [];
    err.missingLabels = data.missing_labels || [];
    err.options = data.options || null;
    throw err;
  }
  return data;
};

export const useTaskStore = defineStore("TaskStore", {
  state: () => ({
    loading: false,
    saving: false,
    // Set to a user-facing string on failure; the component surfaces it.
    error: null,
    // Names of the fields a refused transition is waiting on, so the drawer
    // can highlight them rather than just showing a sentence.
    missing: [],

    // Vocabularies and category field definitions from api/tasks/meta. The
    // create form renders from this instead of a hard-coded copy.
    meta: null,
    // Resolved level and capability set from api/tasks/me.
    session: null,

    rows: [],
    total: 0,
    // The open card: { task, fields, subtasks, files, gates, events, can }.
    current: null,
    people: [],
    profile: null,

    filters: { board: "", status: "", category: "", assignee: "", search: "" },
  }),

  getters: {
    // Cards grouped by lane, for the board. Done once here rather than in a
    // computed per column, so five columns do not each walk the whole list.
    byLane(state) {
      const lanes = {};
      state.rows.forEach((row) => {
        if (!lanes[row.status]) lanes[row.status] = [];
        lanes[row.status].push(row);
      });
      return lanes;
    },
    isLead(state) {
      return !!state.session && (state.session.level === "lead" || state.session.level === "admin");
    },
    // What the current card's state stepper should offer. Each entry carries
    // `allowed` and `missing_labels` straight from the server.
    gates(state) {
      return state.current && state.current.gates ? state.current.gates : {};
    },
  },

  actions: {
    fail(e, fallback) {
      this.error = e && e.message ? e.message : fallback;
      this.missing = e && e.missing ? e.missing : [];
      console.error("[TaskStore]", e);
    },

    async loadSession() {
      try {
        this.session = unwrap(await get(TASKS_ME_ENDPOINT)) || null;
        return this.session;
      } catch (e) {
        this.fail(e, "Could not read your task permissions.");
        return null;
      }
    },

    async loadMeta() {
      if (this.meta) return this.meta;
      try {
        this.meta = unwrap(await get(TASKS_META_ENDPOINT));
        return this.meta;
      } catch (e) {
        this.fail(e, "Could not load the task categories.");
        return null;
      }
    },

    async loadBoard(overrides) {
      this.loading = true;
      this.error = null;
      const params = { ...this.filters, ...(overrides || {}) };
      // Empty strings mean "no filter"; sending them would narrow to a blank
      // municipality and return nothing.
      Object.keys(params).forEach((k) => {
        if (params[k] === "" || params[k] === null) delete params[k];
      });
      // Never send `q`. Drupal routes on $_GET['q'], so a stray q= would not
      // be a filter at all — it would redirect the request to a different
      // path. A filter set named q was the original bug; this also clears it
      // out of any state persisted to localStorage before the rename.
      delete params.q;
      if ("q" in this.filters) delete this.filters.q;
      // "me" needs an identity. The superadmin header bypass resolves to
      // uid 0, where the server reads assignee=me as assignee_uid = 0 and
      // answers with the unassigned cards instead of yours. The filter is
      // persisted, so left alone it sticks across reloads and the board looks
      // permanently short. Drop it rather than send a question the session
      // cannot answer.
      if (params.assignee === "me" && !(this.session && this.session.uid)) {
        delete params.assignee;
        if (this.filters.assignee === "me") this.filters.assignee = "";
      }
      try {
        const data = unwrap(await get(TASKS_LIST_ENDPOINT, null, params));
        this.rows = data.rows || [];
        this.total = data.total || 0;
      } catch (e) {
        this.fail(e, "Could not load the board.");
        this.rows = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    async openCard(ref) {
      this.error = null;
      this.missing = [];
      try {
        this.current = unwrap(await get(TASKS_GET_ENDPOINT, ref));
        return this.current;
      } catch (e) {
        this.fail(e, "Could not open that card.");
        this.current = null;
        return null;
      }
    },

    closeCard() {
      this.current = null;
      this.missing = [];
      this.error = null;
    },

    async createTask(payload) {
      this.saving = true;
      this.error = null;
      this.missing = [];
      try {
        const data = unwrap(await post(TASKS_CREATE_ENDPOINT, null, payload));
        // Put the new card on the board without a refetch; it lands in Triage.
        if (data.row) this.rows.unshift(data.row);
        this.total += 1;
        return data;
      } catch (e) {
        this.fail(e, "Could not create the task.");
        return null;
      } finally {
        this.saving = false;
      }
    },

    async updateTask(ref, payload) {
      this.saving = true;
      this.error = null;
      try {
        const data = unwrap(await post(TASKS_UPDATE_ENDPOINT, ref, payload));
        // Reopen rather than patching locally: the update may have changed
        // what the gates now allow, and that answer belongs to the server.
        await this.openCard(ref);
        await this.loadBoard();
        return data;
      } catch (e) {
        this.fail(e, "Could not save the change.");
        return null;
      } finally {
        this.saving = false;
      }
    },

    // Ask the server to move a card. A refusal is a normal outcome, not a
    // crash: `missing` and `missingLabels` say what is outstanding.
    async transition(ref, toStatus, extra) {
      this.saving = true;
      this.error = null;
      this.missing = [];
      try {
        const data = unwrap(await post(TASKS_TRANSITION_ENDPOINT, ref, {
          to_status: toStatus,
          ...(extra || {}),
        }));
        this.replaceRow(data.row);
        if (this.current && this.current.task && this.current.task.card_ref === ref) {
          await this.openCard(ref);
        }
        return data;
      } catch (e) {
        this.fail(e, "That transition is not available yet.");
        return null;
      } finally {
        this.saving = false;
      }
    },

    async assign(ref, uid) {
      this.saving = true;
      this.error = null;
      try {
        const data = unwrap(await post(TASKS_ASSIGN_ENDPOINT, ref, { assignee_uid: uid }));
        await this.openCard(ref);
        await this.loadBoard();
        return data;
      } catch (e) {
        this.fail(e, "Could not change the assignee.");
        return null;
      } finally {
        this.saving = false;
      }
    },

    // A lead sets the date; an employee proposes one. Which of the two this
    // becomes is the server's decision — pass `propose: true` when the UI is
    // rendering the proposal control, and it still refuses if the account
    // could have set it outright.
    async setDue(ref, date, { propose = false, decide = null, note = "" } = {}) {
      this.saving = true;
      this.error = null;
      try {
        let body;
        if (decide) body = { decide };
        else if (propose) body = { propose: date, note };
        else body = { due_date: date };
        const data = unwrap(await post(TASKS_DUE_ENDPOINT, ref, body));
        await this.openCard(ref);
        await this.loadBoard();
        return data;
      } catch (e) {
        this.fail(e, "Could not change the due date.");
        return null;
      } finally {
        this.saving = false;
      }
    },

    async setBlocked(ref, blocked, reason) {
      this.saving = true;
      this.error = null;
      try {
        const data = unwrap(await post(TASKS_BLOCK_ENDPOINT, ref, { blocked: blocked ? 1 : 0, reason }));
        await this.openCard(ref);
        await this.loadBoard();
        return data;
      } catch (e) {
        this.fail(e, "Could not update the blocked flag.");
        return null;
      } finally {
        this.saving = false;
      }
    },

    async toggleSubtask(sid, done) {
      this.error = null;
      try {
        const data = unwrap(await post(TASKS_SUBTASK_ENDPOINT, sid, { done: done ? 1 : 0 }));
        if (this.current) {
          const sub = (this.current.subtasks || []).find((s) => s.sid === sid);
          if (sub) sub.done = done;
          // Progress feeds the review gate, so refresh the gate block too.
          const ref = this.current.task.card_ref;
          await this.openCard(ref);
        }
        return data;
      } catch (e) {
        this.fail(e, "Could not update the subtask.");
        return null;
      }
    },

    // Attachments. Multipart, so this one call does not go through post().
    // Every file type is accepted; the backend rejects on size only, and
    // reports per-file reasons in `rejected`.
    async uploadFiles(ref, fileList, kind) {
      this.saving = true;
      this.error = null;
      const form = new FormData();
      Array.from(fileList).forEach((f) => form.append("files[]", f));
      if (kind) form.append("kind", kind);
      try {
        const res = await axios.post(url(TASKS_FILE_UPLOAD_ENDPOINT, ref), form, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        const data = unwrap(res);
        if (this.current) this.current.files = data.files || [];
        // A required attachment can be the last thing a gate is waiting on.
        await this.openCard(ref);
        return data;
      } catch (e) {
        this.fail(e, "The upload did not complete.");
        return null;
      } finally {
        this.saving = false;
      }
    },

    async deleteFile(id) {
      this.error = null;
      try {
        const data = unwrap(await post(TASKS_FILE_DELETE_ENDPOINT, id));
        if (this.current) this.current.files = data.files || [];
        return data;
      } catch (e) {
        this.fail(e, "Could not remove the attachment.");
        return null;
      }
    },

    async loadPeople() {
      try {
        const data = unwrap(await get(TASKS_PEOPLE_ENDPOINT));
        this.people = data.people || [];
        return this.people;
      } catch (e) {
        this.fail(e, "Could not load the roster.");
        return [];
      }
    },

    async loadProfile(uid) {
      this.loading = true;
      this.error = null;
      try {
        this.profile = unwrap(await get(TASKS_PERSON_ENDPOINT, uid));
        return this.profile;
      } catch (e) {
        this.fail(e, "Could not load that profile.");
        this.profile = null;
        return null;
      } finally {
        this.loading = false;
      }
    },

    // Swap one row in place so a transition does not reorder the board under
    // the pointer.
    replaceRow(row) {
      if (!row) return;
      const i = this.rows.findIndex((r) => r.tid === row.tid);
      if (i === -1) this.rows.unshift(row);
      else this.rows.splice(i, 1, row);
    },

    setFilter(key, value) {
      this.filters[key] = value;
      return this.loadBoard();
    },
  },

  // Only the vocabularies and the last-used filters. See the note at the top.
  persist: {
    paths: ["meta", "filters"],
  },
});
